//@ts-nocheck
import { SwiperConfigurator } from './swiper-configurator.js';
import { Component } from '@theme/component';
import { ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { debounce, throttle } from './utilities.js';

class PubSubGallery {
  constructor() {
    this.events = {};
  }

  subscribe(event, callback) {
    if (!this.events[event]) {
      this.events[event] = new Set();
    }
    this.events[event].add(callback);
    return () => this.events[event].delete(callback);
  }

  publish(event, data) {
    if (!this.events[event]) return;
    const callbacks = Array.from(this.events[event]);
    for (const callback of callbacks) {
      callback(data);
    }
  }

  hasEvent(event) {
    return !!(this.events[event] && this.events[event].size > 0);
  }
}

const pubSubGallery = new PubSubGallery();

class MediaGalleryAlternative extends Component {
  requiredRefs = ['alternativeSwiper', 'alternativeSwiperButton'];

  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section');
    if (closestSection) {
      closestSection.addEventListener(ThemeEvents.variantSelected, this.updateSlider);
    }

    this.medialightbox = closestSection?.querySelector('media-lightbox');
    this.mediaSwiper = this.refs.alternativeSwiper;
    this.mediaSwiperButtons = this.refs.alternativeSwiperButton;

    this.zoom = this.dataset.isZoom === 'true' || false;

    this.handleChangeVariant = this.changeVariant.bind(this);

    this.observeClassAdded(this.mediaSwiper, () => {
      this.attachEvents(this.mediaSwiper.swiper);
    });

    this.mediaSwiperButtons.forEach((button) => button.addEventListener('click', this.handleChangeVariant));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (closestSection) {
      closestSection.removeEventListener(ThemeEvents.variantSelected, this.updateSlider);
    }
    this.mediaSwiperButtons.forEach((button) => button.removeEventListener('click', this.handleChangeVariant));
  }

  updateSlider = () => {
    const { alternativeSwiper } = this.refs;
    if (alternativeSwiper.swiper && typeof alternativeSwiper.swiper.destroy === 'function') {
      alternativeSwiper.swiper.destroy();
      this.mediaSwiperButtons[1]?.removeAttribute('data-change-next');
    }
    if (alternativeSwiper instanceof HTMLElement) {
      this.mediaSwiperButtons[1]?.removeAttribute('data-change-next');

      new SwiperConfigurator(alternativeSwiper);

      this.attachEvents(alternativeSwiper.swiper);

      if (!pubSubGallery.hasEvent('changeSlide')) {
        pubSubGallery.subscribe('changeSlide', (currentPosition) => {
          setTimeout(() => {
            if (alternativeSwiper && alternativeSwiper.swiper) {
              const { slides } = alternativeSwiper.swiper;
              const index = [...slides].findIndex((slide) => slide.getAttribute('data-position') === currentPosition);

              alternativeSwiper.swiper.slideTo(Math.max(index, 0), 300);
            }
          }, 1000);
        });
      }
    }
  };

  getRelevantIds() {
    return {
      next: this.dataset.nextVariant,
      prev: this.dataset.prevVariant,
      current: this.dataset.currentVariant,
    };
  }

  triggerVariantImageChange(currentId) {
    const variantPickerImage = document.querySelector(`.variant-image-picker input[data-variant-id='${currentId}']`);

    const changeEvent = new Event('change', {
      bubbles: true,
      cancelable: true,
    });

    if (variantPickerImage) {
      variantPickerImage.dispatchEvent(changeEvent);
    }
  }

  changeVariant(event) {
    const { alternativeSwiper } = this.refs;

    if (!alternativeSwiper.swiper) {
      console.error('Swiper is not initialized');
      return;
    }
    const { isBeginning, isEnd } = alternativeSwiper.swiper;
    const button = event.target.closest('button');
    const { name } = button;

    const ids = this.getRelevantIds();
    const currentId = ids[name] || ids['current'];

    const shouldChangeSlide = button.hasAttribute(`data-change-${name}`);

    if (shouldChangeSlide) this.triggerVariantImageChange(currentId);

    this.mediaSwiperButtons[0]?.toggleAttribute('data-change-prev',isBeginning);
    this.mediaSwiperButtons[1]?.toggleAttribute('data-change-next',isEnd);

    // if (name == 'prev') button.toggleAttribute('data-change-prev', isBeginning);
    // if (name == 'next') button.toggleAttribute('data-change-next', isEnd);
  }

  observeClassAdded(element, onClassAdded) {
    let called = false;

    const runOnce = () => {
      if (called) return;
      called = true;
      observer.disconnect();
      onClassAdded(element);
    };

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (
          mutation.type === 'attributes' &&
          mutation.attributeName === 'class' &&
          element.classList.contains('swiper-initialized')
        ) {
          runOnce();
        }
      }
    });

    observer.observe(element, {
      attributes: true,
      attributeFilter: ['class'],
    });

    if (element.classList.contains('swiper-initialized')) {
      runOnce();
    }
  }

  attachEvents(swiper) {
    if (!swiper) {
      console.error('Swiper not found');
      return;
    }

    if (this.zoom === true) {
      swiper.on('click', (event) => {
        const { clickedSlide } = event;

        if (this.medialightbox && typeof this.medialightbox.toggleMode == 'function') {
          this.medialightbox.toggleMode();
          this.medialightbox.sliderTo(clickedSlide);
        }
      });
    }

    swiper.on('slideResetTransitionStart', (event) => {
      const ids = this.getRelevantIds();

      const {
        swipeDirection,
        navigation: { nextEl, prevEl },
      } = event;

      const isButtonDisabled = (el) => el?.classList.contains('swiper-button-disabled');

      const handleSwipeEnd = (direction, buttonEl, fallbackId) => {
        if (swipeDirection === direction && isButtonDisabled(buttonEl)) {
          const currentId = ids[direction] || ids[fallbackId];
          this.triggerVariantImageChange(currentId);
        }
      };

      handleSwipeEnd('next', nextEl, 'current');
      handleSwipeEnd('prev', prevEl, 'current');
    });
  }
}

if (!customElements.get('media-gallery-alternative')) {
  customElements.define('media-gallery-alternative', MediaGalleryAlternative);
}

class Medialightbox extends Component {
  #SLIDER_TYPES = ['thumb', 'main'];
  #hasInsertedImages = false;

  connectedCallback() {
    super.connectedCallback();

    this.hidePanelTimer = null;
    this.lightboxPanel = this.querySelector('.lightbox-panel');
    this.currentCounter = this.querySelector('#current-counter');
    this.actionButtons = this.querySelectorAll('.lightbox-action button');

    this.templates = {
      main: this.querySelector('template#main-images'),
      thumb: this.querySelector('template#thumb-images'),
    };

    this.containers = {
      main: this.querySelector('.lightbox-main__swiper'),
      thumb: this.querySelector('.lightbox-thumb__swiper'),
    };

    this.configs = {
      main: this.parseConfig(this.dataset.mainSlider),
      thumb: this.parseConfig(this.dataset.thumbSlider),
    };

    this.sliders = {
      main: null,
      thumb: null,
    };

    this.handleActionPanel = this.panelAction.bind(this);
    this.handleUpdateSlider = this.updateSlider.bind(this);
    this.handleMoving = this.onMoving.bind(this);

    const closestSection = this.closest('.shopify-section');
    if (closestSection) {
      closestSection.addEventListener(ThemeEvents.variantSelected, this.handleUpdateSlider);
    }

    this.actionButtons.forEach((button) => button.addEventListener('click', this.handleActionPanel));

    if (!this.isApiFullScreen()) {
      this.getButton('screen')?.classList.add('lightbox-action__button--hidden');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (closestSection) {
      closestSection.removeEventListener(ThemeEvents.variantSelected, this.handleUpdateSlider);
    }
    this.actionButtons.forEach((button) => button.removeEventListener('click', this.handleActionPanel));

    this.toggleWindowEvent(false);
  }

  onMoving() {
    this.classList.add('media-lightbox--visible-elements');

    if (this.hidePanelTimer) clearTimeout(this.hidePanelTimer);

    this.hidePanelTimer = setTimeout(() => {
      this.classList.remove('media-lightbox--visible-elements');
    }, 3000);
  }

  updateSlider(event) {
    this.#hasInsertedImages = false;

    const destroySlider = (type) => {
      const currentSlider = this.sliders[type];
      const currentContainer = this.containers[type].querySelector('swiper-wrapper');
      if (currentContainer) currentContainer.innerHTML = '';
      if (currentSlider && typeof currentSlider.destroy === 'function') currentSlider.destroy();
    };

    destroySlider('thumb');
    destroySlider('main');
    this.toggleWindowEvent(false);
  }

  toggleMode() {
    this.insertContentIfNeeded();
    const isActive = this.classList.contains('media-lightbox--active');
    this.classList.toggle('media-lightbox--active', !isActive);
    this.toggleWindowEvent(!isActive);
  }

  insertContentIfNeeded() {
    if (this.#hasInsertedImages) return;

    this.renderImages();
    this.initializeSliders();
    this.#hasInsertedImages = true;
  }

  initializeSliders() {
    if (typeof Swiper !== 'function') {
      console.error('Swiper is not loaded.');
      return;
    }

    const initSlider = (type) => {
      const container = this.containers[type];
      const config = this.configs[type];

      if (!container) {
        console.warn(`Container for "${type}" not found.`);
        return null;
      }
      if (!config) {
        console.warn(`Config for "${type}" not found.`);
        return null;
      }

      return new Swiper(container, config);
    };

    const thumbsSwiper = initSlider('thumb');
    if (!thumbsSwiper) return;

    const mainSwiper = initSlider('main');
    if (!mainSwiper) return;

    this.sliders['thumb'] = thumbsSwiper;
    this.sliders['main'] = mainSwiper;

    mainSwiper.thumbs.swiper = thumbsSwiper;
    mainSwiper.thumbs.init();
    mainSwiper.thumbs.update();

    this.attachEvents(mainSwiper);
  }

  getTemplateImages(template) {
    return template?.content.cloneNode(true).children || [];
  }

  renderImages() {
    for (const type of this.#SLIDER_TYPES) {
      const template = this.templates[type];
      const container = this.containers[type]?.querySelector('.swiper-wrapper');

      if (template && container) {
        const images = this.getTemplateImages(template);
        [...images].forEach((image) => container.appendChild(image));
      }
    }

    console.log('Images were inserted');
  }

  parseConfig(configString) {
    try {
      return JSON.parse(configString) || {};
    } catch {
      console.warn('Failed to parse slider config:', configString);
      return {};
    }
  }

  toggleWindowEvent(shouldAdd) {
    if (this.isTouchDevice()) {
      this.classList.add('media-lightbox--visible-elements');
      return;
    }

    if (shouldAdd) {
      window.addEventListener('mousemove', this.handleMoving, { passive: true });
    } else {
      window.removeEventListener('mousemove', this.handleMoving);
    }
  }

  attachEvents(slider) {
    if (!slider) {
      console.error('slider is undefined or null');
      return;
    }

    slider.on('slideChange', (event) => {
      const { realIndex } = event;
      if (this.currentCounter) this.currentCounter.textContent = realIndex ? Number(realIndex) + 1 : 1;
    });

    slider.on('click', (event) => {
      event.zoom.toggle();
    });

    slider.on('zoomChange', (event) => {
      const { clickedSlide, activeIndex, realIndex, slides } = event;

      const activeSlide = slides[realIndex] || clickedSlide;

      const zoomButton = this.getButton('zoom');

      if (!zoomButton || !activeSlide) {
        console.error('ZoomButton or ActiveSlide is missing');
        return;
      }

      setTimeout(() => {
        const isZoomed = activeSlide.classList.contains('swiper-slide-zoomed');
        zoomButton.classList.toggle('lightbox-action__button--active', isZoomed);
      }, 100);
    });
  }

  handleZoom() {
    if (!this.sliders.main) {
      console.error('Main slider is not initialized');
      return;
    }

    this.sliders.main.zoom.toggle(this.sliders.main, 3);
  }

  handleScreen() {
    const documentElement = document.documentElement;
    const doc = document;

    const isFullscreen = !!(doc.fullscreenElement || doc.webkitFullscreenElement || doc.msFullscreenElement);

    const requestFullscreen = this.isApiFullScreen();
    const exitFullscreen = doc.exitFullscreen || doc.webkitExitFullscreen || doc.msExitFullscreen;

    if (!requestFullscreen || !exitFullscreen) {
      console.error('Fullscreen API is not supported in this browser.');
      return;
    }

    try {
      if (!isFullscreen) {
        requestFullscreen.call(documentElement);
      } else {
        exitFullscreen.call(doc);
      }
    } catch (err) {
      console.error('Fullscreen toggle error:', err);
    }
  }

  handleClose() {
    const { main } = this.sliders;

    const closeModal = () => {
      this.classList.remove('media-lightbox--active');
      this.toggleWindowEvent(false);
    };

    if (!main) {
      console.error('Main swiper is null');
      closeModal();
      return;
    }

    const { slides, realIndex } = main;
    const closedSlide = slides?.[realIndex];

    if (!closedSlide) {
      closeModal();
      return;
    }

    const {
      dataset: { variant, currentPosition },
    } = closedSlide;

    const shouldChangeSlide = variant !== this.getAttribute('data-current-id');

    if (variant && shouldChangeSlide === true) {
      const variantPickerInput = document.querySelector(`.variant-image-picker input[data-variant-id='${variant}']`);

      if (variantPickerInput) {
        variantPickerInput.dispatchEvent(new Event('change', { bubbles: true, cancelable: true }));
      }

      pubSubGallery.publish('changeSlide', currentPosition);
    } else if (shouldChangeSlide === false) {
      const mediaSlider = document.querySelector('media-gallery-alternative .swiper');
      if (mediaSlider && mediaSlider.swiper) {
        const { slides } = mediaSlider.swiper;
        const index = [...slides].findIndex((slide) => slide.getAttribute('data-position') === currentPosition);
        mediaSlider.swiper.slideTo(Math.max(index, 0), 0);
      }
    }

    closeModal();
  }

  panelAction(event) {
    const { name } = event.target;
    const button = event.target.closest('button');

    const action = {
      zoom: () => this.handleZoom(),
      screen: () => this.handleScreen(),
      close: () => this.handleClose(),
    };

    const toggleMode = (button) => {
      if (!button || !(button instanceof HTMLButtonElement)) {
        console.error('Button is undefined or not an HTMLButtonElement');
        return;
      }
      const isActive = button.classList.contains('lightbox-action__button--active');
      button.classList.toggle('lightbox-action__button--active', !isActive);
    };

    if (!action[name]) {
      console.error('Action is undefined or null');
      return;
    }
    action[name]();
    if (name !== 'zoom') {
      toggleMode(button);
    }
  }

  sliderTo(clickedSlide) {
    if (!clickedSlide) {
      console.error('ClickedSlide is undefined or null');
      return;
    }

    if (!this.sliders.main) {
      console.error('Main slider is not initialized');
      return;
    }

    const position = Number(clickedSlide.dataset?.position);

    if (isNaN(position)) {
      console.error('Position is not a valid number');
      return;
    }

    this.sliders.main.slideTo(Math.max(position - 1, 0), 0);
  }

  getButton(type) {
    return [...this.actionButtons].find(({ name }) => name === type);
  }

  isTouchDevice() {
    return !window.matchMedia('(hover: hover)').matches;
  }

  isApiFullScreen() {
    try {
      return (
        document.documentElement.requestFullscreen ||
        document.documentElement.webkitRequestFullscreen ||
        document.documentdocumentElement.msRequestFullscreen
      );
    } catch (error) {
      console.error(error);
      return false;
    }
  }
}
if (!customElements.get('media-lightbox')) {
  customElements.define('media-lightbox', Medialightbox);
}
